import { Tooltip } from '@material-ui/core';
import { Inline, Tag } from '@superdispatch/ui';
import {
  LoadSuggestionAlgorithm,
  loadSuggestionAlgorithmsDescriptionMap,
  SHIPPER_RECOMMENDED_ALGORITHM,
} from './data/LoadSuggestionDTO';
import { useMemo } from 'react';
import styled from 'styled-components';
import { SuggestedByShipperTag } from './SuggestedByShipperTag';

const TAGS_LIMIT = 2;

const TooltipList = styled.ul`
  margin: 0;
  padding-inline-start: 16px;
`;

const MoreTag = styled(Tag)`
  cursor: pointer;
`;

interface LoadSuggestionsAlgorithmTagsProps {
  algorithms?: LoadSuggestionAlgorithm[];
}

export function LoadSuggestionsAlgorithmTags({
  algorithms,
}: LoadSuggestionsAlgorithmTagsProps) {
  const hasShipperRecommended = !!algorithms?.includes(
    SHIPPER_RECOMMENDED_ALGORITHM,
  );

  const tags = useMemo(
    () =>
      algorithms
        ?.filter(
          (
            algorithm,
          ): algorithm is Exclude<
            LoadSuggestionAlgorithm,
            typeof SHIPPER_RECOMMENDED_ALGORITHM
          > => algorithm !== SHIPPER_RECOMMENDED_ALGORITHM,
        )
        .map(
          (algorithm) => loadSuggestionAlgorithmsDescriptionMap[algorithm],
        ) ?? [],
    [algorithms],
  );

  if (!hasShipperRecommended && !tags.length) {
    return null;
  }

  const tagsLimit = hasShipperRecommended ? TAGS_LIMIT - 1 : TAGS_LIMIT;
  const visibleTags =
    tags.length > tagsLimit ? tags.slice(0, tagsLimit - 1) : tags;
  const hiddenTags = tags.slice(visibleTags.length);

  return (
    <Inline space="xsmall">
      {hasShipperRecommended && <SuggestedByShipperTag />}

      {visibleTags.map((tag, idx) => (
        <Tag key={idx} variant="subtle" color="grey">
          {tag}
        </Tag>
      ))}

      {hiddenTags.length > 0 && (
        <Tooltip
          arrow={true}
          title={
            <TooltipList>
              {hiddenTags.map((tag, idx) => (
                <li key={idx}>{tag}</li>
              ))}
            </TooltipList>
          }
        >
          <MoreTag variant="subtle" color="grey">
            +{hiddenTags.length} more
          </MoreTag>
        </Tooltip>
      )}
    </Inline>
  );
}
